'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { useLogin, useLogout, useProfile } from '@/hooks/useApi';
import type { User, LoginCredentials } from '@/types';
import { UserRole } from '@/types';

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (credentials: LoginCredentials) => Promise<User | null>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const loginMutation = useLogin();
    const logoutMutation = useLogout();

    // Only fetch profile if we have a token (configured in useProfile hook)
    const { data: profileData, isLoading: profileLoading, refetch: refetchProfile } = useProfile();

    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            setUser(null);
            setIsLoading(false);
            return;
        }
        if (profileLoading) {
            setIsLoading(true);
            return;
        }
        if (profileData) {
            setUser(profileData);
        } else {
            setUser(null);
        }
        setIsLoading(false);
    }, [profileData, profileLoading]);

    const login = async (credentials: LoginCredentials) => {
        try {
            const data = await loginMutation.mutateAsync(credentials);
            localStorage.setItem('access_token', data.access_token);
            const result = await refetchProfile();
            const nextUser = result.data ?? null;
            setUser(nextUser);
            return nextUser;
        } catch (error) {
            throw error;
        }
    };

    const logout = async () => {
        try {
            await logoutMutation.mutateAsync();
            setUser(null);
            localStorage.removeItem('access_token');
        } catch (error) {
            // Even if logout fails, clear local state
            setUser(null);
            localStorage.removeItem('access_token');
        }
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated: !!user,
                isLoading,
                login,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Protected Route Component
export const ProtectedRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { isAuthenticated, isLoading, user } = useAuth();
    const router = useRouter();
    const isAdmin = !!user?.roles?.some((role) => role === UserRole.ADMIN || role === UserRole.MEDIATOR);

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <div className="skeleton" style={{ width: '200px', height: '40px', borderRadius: '8px' }} />
            </div>
        );
    }

    if (!isAuthenticated) {
        router.replace('/gemp-admin/login');
        return null;
    }
    if (!isAdmin) {
        localStorage.removeItem('access_token');
        router.replace('/gemp-admin/login');
        return null;
    }

    return <>{children}</>;
};
